'use server'

import { apiRequest, ApiResponse } from '../request'

export type GuideStatus =
    | 'GUIDE_DRAFT'
    | 'GUIDE_PENDING_AUTHORIZATION'
    | 'GUIDE_AUTHORIZED'
    | 'GUIDE_DENIED'
    | 'GUIDE_PENDING_SUBMISSION'
    | 'GUIDE_SUBMITTED'
    | 'GUIDE_PROCESSED'
    | 'GUIDE_GLOSS'
    | 'GUIDE_PAID'
    | 'GUIDE_CANCELLED'

export type GuideOrigin =
    | 'CONSULTATION'
    | 'PROCEDURE'
    | 'EXAM'
    | 'HOSPITALIZATION'
    | 'MANUAL'

export type GuideType =
    | 'CONSULTA'
    | 'SADT'
    | 'INTERNACAO'
    | 'HONORARIOS'
    | 'ANEXO_OPME'
    | 'ANEXO_QUIMIO'

export interface TissGuide {
    id: number
    companyId: number
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
        ansCode: string
    }
    clientId: number
    client?: {
        id: number
        name: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
    }
    consultationId?: number
    orderId?: number
    examId?: number
    billingBatchId?: number
    guideNumber: string
    guideType: GuideType
    origin: GuideOrigin
    status: GuideStatus
    operatorNumber?: string
    authorizationNumber?: string
    authorizationDate?: string
    requestDate: string
    executionDate?: string
    validUntil?: string
    mainOrderCode?: string
    mainOrderName?: string
    totalRequested: number
    totalAuthorized?: number
    totalGloss?: number
    totalPaid?: number
    submissionDate?: string
    submissionProtocol?: string
    xmlContent?: string
    deleted: boolean
    createdAt: string
    updatedAt: string
    items?: TissGuideItem[]
    history?: TissGuideHistory[]
    _count?: {
        items: number
        glosses: number
    }
}

export interface TissGuideItem {
    id: number
    guideId: number
    procedureCode: string
    procedureName: string
    quantity: number
    unitValue: number
    totalValue: number
    authorizedQuantity?: number
    authorizedValue?: number
    executionDate?: string
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface TissGuideHistory {
    id: number
    guideId: number
    userId?: number
    user?: {
        id: number
        name: string
    }
    action: string
    previousStatus?: string
    newStatus?: string
    notes?: string
    metadata?: any
    createdAt: string
}

export interface CreateGuideData {
    healthPlanId: number
    clientId: number
    professionalId?: number
    guideType: GuideType
    origin: GuideOrigin
    requestDate?: string
    mainOrderCode?: string
    mainOrderName?: string
    items?: Array<{
        procedureCode: string
        procedureName: string
        quantity: number
        unitValue: number
        executionDate?: string
        notes?: string
    }>
}

export interface UpdateGuideData {
    guideType?: GuideType
    operatorNumber?: string
    authorizationNumber?: string
    authorizationDate?: string
    executionDate?: string
    validUntil?: string
    mainOrderCode?: string
    mainOrderName?: string
}

export interface GuideFilters {
    healthPlanId?: number
    clientId?: number
    professionalId?: number
    status?: GuideStatus
    guideType?: GuideType
    origin?: GuideOrigin
    startDate?: string
    endDate?: string
    search?: string
}

export interface CreateGuideFromServiceData {
    healthPlanId: number
    guideType?: GuideType
}

// Guide CRUD
export async function getGuides(
    filters?: GuideFilters
): Promise<ApiResponse<TissGuide[]>> {
    return apiRequest<TissGuide[]>('billing/guides', { params: filters })
}

export async function getGuide(id: number): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(`billing/guides/${id}`, {})
}

export async function createGuide(
    data: CreateGuideData
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>('billing/guides', { data }, 'post')
}

export async function updateGuide(
    id: number,
    data: UpdateGuideData
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(`billing/guides/${id}`, { data }, 'patch')
}

export async function deleteGuide(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`billing/guides/${id}`, {}, 'delete')
}

// Create from services
export async function createGuideFromConsultation(
    consultationId: number,
    data: CreateGuideFromServiceData
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/from-consultation/${consultationId}`,
        { data },
        'post'
    )
}

export async function createGuideFromOrder(
    orderId: number,
    data: CreateGuideFromServiceData
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/from-order/${orderId}`,
        { data },
        'post'
    )
}

export async function createGuideFromExam(
    examId: number,
    data: CreateGuideFromServiceData
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/from-exam/${examId}`,
        { data },
        'post'
    )
}

// Guide items
export async function addGuideItem(
    guideId: number,
    data: {
        procedureCode: string
        procedureName: string
        quantity: number
        unitValue: number
        executionDate?: string
        notes?: string
    }
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${guideId}/items`,
        { data },
        'post'
    )
}

export async function updateGuideItem(
    guideId: number,
    itemId: number,
    data: Partial<{
        procedureCode: string
        procedureName: string
        quantity: number
        unitValue: number
        authorizedQuantity: number
        authorizedValue: number
        executionDate: string
        notes: string
    }>
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${guideId}/items/${itemId}`,
        { data },
        'patch'
    )
}

export async function removeGuideItem(
    guideId: number,
    itemId: number
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${guideId}/items/${itemId}`,
        {},
        'delete'
    )
}

// Guide workflow
export async function submitGuideForAuthorization(
    id: number
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${id}/submit-authorization`,
        {},
        'post'
    )
}

export async function authorizeGuide(
    id: number,
    data: {
        authorizationNumber?: string
        operatorNumber?: string
        validUntil?: string
    }
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(`billing/guides/${id}/authorize`, { data }, 'post')
}

export async function denyGuide(
    id: number,
    reason: string
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${id}/deny`,
        { data: { reason } },
        'post'
    )
}

export async function submitGuide(id: number): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(`billing/guides/${id}/submit`, {}, 'post')
}

export async function cancelGuide(
    id: number,
    reason?: string
): Promise<ApiResponse<TissGuide>> {
    return apiRequest<TissGuide>(
        `billing/guides/${id}/cancel`,
        { data: { reason } },
        'post'
    )
}

// XML generation
export async function generateGuideXml(id: number): Promise<ApiResponse<string>> {
    return apiRequest<string>(`billing/guides/${id}/xml`, {})
}

// Guide history
export async function getGuideHistory(
    id: number
): Promise<ApiResponse<TissGuideHistory[]>> {
    return apiRequest<TissGuideHistory[]>(`billing/guides/${id}/history`, {})
}

// Labels
export const GUIDE_STATUS_LABELS: Record<GuideStatus, string> = {
    GUIDE_DRAFT: 'Rascunho',
    GUIDE_PENDING_AUTHORIZATION: 'Aguardando Autorizacao',
    GUIDE_AUTHORIZED: 'Autorizada',
    GUIDE_DENIED: 'Negada',
    GUIDE_PENDING_SUBMISSION: 'Aguardando Envio',
    GUIDE_SUBMITTED: 'Enviada',
    GUIDE_PROCESSED: 'Processada',
    GUIDE_GLOSS: 'Glosada',
    GUIDE_PAID: 'Paga',
    GUIDE_CANCELLED: 'Cancelada',
}

export const GUIDE_STATUS_COLORS: Record<GuideStatus, string> = {
    GUIDE_DRAFT: 'badge-ghost',
    GUIDE_PENDING_AUTHORIZATION: 'badge-warning',
    GUIDE_AUTHORIZED: 'badge-success',
    GUIDE_DENIED: 'badge-error',
    GUIDE_PENDING_SUBMISSION: 'badge-info',
    GUIDE_SUBMITTED: 'badge-info',
    GUIDE_PROCESSED: 'badge-success',
    GUIDE_GLOSS: 'badge-error',
    GUIDE_PAID: 'badge-success',
    GUIDE_CANCELLED: 'badge-ghost',
}

export const GUIDE_ORIGIN_LABELS: Record<GuideOrigin, string> = {
    CONSULTATION: 'Consulta',
    PROCEDURE: 'Procedimento',
    EXAM: 'Exame',
    HOSPITALIZATION: 'Internacao',
    MANUAL: 'Manual',
}

export const GUIDE_TYPE_LABELS: Record<GuideType, string> = {
    CONSULTA: 'Consulta',
    SADT: 'SP/SADT',
    INTERNACAO: 'Internacao',
    HONORARIOS: 'Honorarios',
    ANEXO_OPME: 'Anexo OPME',
    ANEXO_QUIMIO: 'Anexo Quimioterapia',
}
